import {
  MarketTickerTapeWidget,
  MarketChartWidget,
  MarketSymbolWidget,
  MarketHeatmapWidget,
  MarketCalendarWidget,
  MarketNewsWidget,
  MarketMoversWidget,
  MarketTechnicalsWidget,
  WATCHLISTS,
  HEATMAP_OPTIONS,
  CALENDAR_IMPORTANCE,
  CALENDAR_COUNTRIES,
  NEWS_FEEDS,
} from './marketWidgets';

/**
 * "Market" widgets (TradingView embeds). Same entry shape as widgetRegistry.js.
 * Symbols use TradingView's EXCHANGE:TICKER form (NASDAQ:AAPL, BITSTAMP:BTCUSD).
 */

const SYMBOL_FIELD = (fallback, label = 'Symbol') => ({
  key: 'symbol',
  label: `${label} (e.g. ${fallback})`,
  type: 'text',
  maxLength: 40,
  defaultValue: fallback,
});

const WATCHLIST_FIELD = {
  key: 'watchlist',
  label: 'Watchlist',
  type: 'select',
  noBlank: true,
  options: [
    ...Object.entries(WATCHLISTS).map(([value, w]) => ({ value, label: w.label })),
    { value: 'custom', label: 'My own symbols' },
  ],
  defaultValue: 'indices',
};

const CHART_INTERVALS = [
  { value: '5', label: '5 minutes' },
  { value: '15', label: '15 minutes' },
  { value: '60', label: '1 hour' },
  { value: '240', label: '4 hours' },
  { value: 'D', label: '1 day' },
  { value: 'W', label: '1 week' },
];

const MARKET_WIDGET_REGISTRY = {
  mkTickerTape: {
    label: 'Ticker tape',
    description: 'Scrolling live quotes: indices, futures, big tech, crypto or your own list.',
    component: MarketTickerTapeWidget,
    defaultSize: { w: 12, h: 1 },
    minSize: { w: 4, h: 1 },
    configFields: () => [
      WATCHLIST_FIELD,
      {
        key: 'symbols',
        label: 'Your symbols, comma separated (e.g. NASDAQ:AAPL, AMEX:SPY, BITSTAMP:BTCUSD). CME futures like ES1! aren’t available in TradingView embeds; use OANDA:SPX500USD or OANDA:NAS100USD instead.',
        type: 'text',
        maxLength: 400,
        showWhen: (c) => c?.watchlist === 'custom',
      },
    ],
    defaultConfig: () => ({ watchlist: 'indices', symbols: '' }),
  },
  mkChart: {
    label: 'Live chart',
    description: 'A full TradingView chart for any symbol, with indicators.',
    component: MarketChartWidget,
    defaultSize: { w: 8, h: 6 },
    minSize: { w: 4, h: 4 },
    configFields: () => [
      SYMBOL_FIELD('AMEX:SPY'),
      { key: 'interval', label: 'Candle size', type: 'select', noBlank: true, options: CHART_INTERVALS, defaultValue: 'D' },
    ],
    defaultConfig: () => ({ symbol: 'AMEX:SPY', interval: 'D' }),
  },
  mkSymbol: {
    label: 'Price card',
    description: 'One symbol’s price, change and a small chart.',
    component: MarketSymbolWidget,
    defaultSize: { w: 3, h: 3 },
    minSize: { w: 2, h: 2 },
    configFields: () => [
      SYMBOL_FIELD('FOREXCOM:SPXUSD'),
      {
        key: 'range', label: 'Period', type: 'select', noBlank: true, defaultValue: '1M',
        options: [{ value: '1D', label: '1 day' }, { value: '1M', label: '1 month' }, { value: '12M', label: '1 year' }, { value: '60M', label: '5 years' }],
      },
    ],
    defaultConfig: () => ({ symbol: 'FOREXCOM:SPXUSD', range: '1M' }),
  },
  mkHeatmap: {
    label: 'Market heatmap',
    description: 'Today’s winners and losers at a glance, sized by market cap.',
    component: MarketHeatmapWidget,
    defaultSize: { w: 6, h: 5 },
    minSize: { w: 4, h: 3 },
    configFields: () => [{ key: 'market', label: 'Market', type: 'select', noBlank: true, options: HEATMAP_OPTIONS, defaultValue: 'SPX500' }],
    defaultConfig: () => ({ market: 'SPX500' }),
  },
  mkCalendar: {
    label: 'Economic calendar',
    description: 'Upcoming CPI, jobs reports, Fed meetings and other market-moving events.',
    component: MarketCalendarWidget,
    defaultSize: { w: 4, h: 6 },
    minSize: { w: 3, h: 4 },
    configFields: () => [
      { key: 'importance', label: 'Events', type: 'select', noBlank: true, options: CALENDAR_IMPORTANCE, defaultValue: '0,1' },
      { key: 'countries', label: 'Countries', type: 'select', noBlank: true, options: CALENDAR_COUNTRIES, defaultValue: 'us' },
    ],
    defaultConfig: () => ({ importance: '0,1', countries: 'us' }),
  },
  mkNews: {
    label: 'Market news',
    description: 'Headlines for the whole market, a sector, or one symbol.',
    component: MarketNewsWidget,
    defaultSize: { w: 4, h: 6 },
    minSize: { w: 3, h: 4 },
    configFields: () => [
      { key: 'feed', label: 'News', type: 'select', noBlank: true, options: NEWS_FEEDS, defaultValue: 'all' },
      { ...SYMBOL_FIELD('NASDAQ:AAPL'), defaultValue: '', showWhen: (c) => c?.feed === 'symbol' },
    ],
    defaultConfig: () => ({ feed: 'all', symbol: '' }),
  },
  mkMovers: {
    label: 'Top movers',
    description: 'US stocks moving the most: gainers, losers and most active.',
    component: MarketMoversWidget,
    defaultSize: { w: 4, h: 6 },
    minSize: { w: 3, h: 4 },
    configFields: () => [{
      key: 'range', label: 'Chart period', type: 'select', noBlank: true, defaultValue: '1D',
      options: [{ value: '1D', label: '1 day' }, { value: '1M', label: '1 month' }, { value: '12M', label: '1 year' }],
    }],
    defaultConfig: () => ({ range: '1D' }),
  },
  mkTechnicals: {
    label: 'Technical rating',
    description: 'Buy / sell gauge from moving averages and oscillators for one symbol.',
    component: MarketTechnicalsWidget,
    defaultSize: { w: 4, h: 5 },
    minSize: { w: 3, h: 4 },
    configFields: () => [
      SYMBOL_FIELD('AMEX:SPY'),
      {
        key: 'interval', label: 'Timeframe', type: 'select', noBlank: true, defaultValue: '1D',
        options: [{ value: '15m', label: '15 minutes' }, { value: '1h', label: '1 hour' }, { value: '4h', label: '4 hours' }, { value: '1D', label: '1 day' }, { value: '1W', label: '1 week' }],
      },
    ],
    defaultConfig: () => ({ symbol: 'AMEX:SPY', interval: '1D' }),
  },
};

export const MARKET_WIDGET_TYPES = Object.entries(MARKET_WIDGET_REGISTRY).map(([type, meta]) => ({
  type,
  label: meta.label,
  description: meta.description,
}));

export function getMarketWidgetMeta(type) {
  return MARKET_WIDGET_REGISTRY[type] || null;
}

export function isMarketWidgetType(type) {
  return Boolean(type && MARKET_WIDGET_REGISTRY[type]);
}

export default MARKET_WIDGET_REGISTRY;
